import { useState, useMemo, useEffect, useCallback } from 'react'
import PropTypes from 'prop-types'
import { useDispatch } from 'react-redux'

import { STRING, EVENT_TYPE } from 'resources'

import { sendAnalyticsEvent } from 'services/analytics'

import profileInfoActions from 'models/ProfileInfo/actions'
import { IDENTIFIER_ADD_ENDPOINT_ERROR_CODES } from 'models/ProfileInfo/constants'

import { formatEmailAddress, validateEmail } from 'utils/email'

import { ModalTitle } from 'components/ReklaimModal'
import TextInput from 'components/TextInput'

import { ModalDescription, Form, Button } from './styles'

const EmailAddressAddForm = ({
  emailAddressData,
  setPendingEmailAddress,
  setScreenToEmailConfirmation,
  autoSubmitEmailAddress = null,
}) => {
  const dispatch = useDispatch()

  const [rawEmailAddress, setRawEmailAddress] = useState('')
  const [isEmailSubmitting, setIsEmailSubmitting] = useState(false)
  const [isSendVerificationEmailFailed, setSendVerificationEmailFailed] = useState(false)
  const [errorCode, setErrorCode] = useState('')
  const [hasAutoSubmitted, setHasAutoSubmitted] = useState(false)

  useEffect(() => {
    sendAnalyticsEvent(EVENT_TYPE.accountAddEmailAddressModalEnterEmailAddressView)
  }, [])

  const invalidEmailAddress = useMemo(() => !validateEmail(rawEmailAddress), [rawEmailAddress])
  const isSameEmailAddress = useMemo(
    () => emailAddressData.some((verifiedEmailAddress) => verifiedEmailAddress.emailAddress === rawEmailAddress),
    [rawEmailAddress, emailAddressData],
  )

  const handleEmailChange = (event) => {
    setRawEmailAddress(event.target.value)

    if (isSendVerificationEmailFailed) {
      setSendVerificationEmailFailed(false)
      setErrorCode('')
    }
  }

  const onEmailSubmit = useCallback(
    (emailAddress) => {
      sendAnalyticsEvent(EVENT_TYPE.accountAddEmailAddressModalEnterEmailAddressSubmitClick, {
        emailAddress,
      })
      setIsEmailSubmitting(true)
      dispatch(
        profileInfoActions.addEmailAddressSendVerificationEmail.init(
          emailAddress,
          'protect',
          (isSuccess, apiErrorCode) => {
            setIsEmailSubmitting(false)
            if (isSuccess) {
              sendAnalyticsEvent(EVENT_TYPE.accountAddEmailAddressModalEnterEmailAddressSubmitSuccess, {
                emailAddress,
              })
              setPendingEmailAddress(formatEmailAddress(emailAddress))
              setScreenToEmailConfirmation()
            } else {
              sendAnalyticsEvent(EVENT_TYPE.accountAddEmailAddressModalEnterEmailAddressSubmitFailure, {
                emailAddress,
                apiErrorCode,
              })
              setSendVerificationEmailFailed(true)
              setErrorCode(apiErrorCode)
            }
          },
        ),
      )
    },
    [dispatch, setPendingEmailAddress, setScreenToEmailConfirmation, setErrorCode],
  )

  useEffect(() => {
    if (autoSubmitEmailAddress && !hasAutoSubmitted) {
      setRawEmailAddress(autoSubmitEmailAddress)
      onEmailSubmit(autoSubmitEmailAddress)
      setHasAutoSubmitted(true)
    }
  }, [autoSubmitEmailAddress, onEmailSubmit, hasAutoSubmitted])

  const handleSubmitClick = (event) => {
    event.preventDefault()
    if (invalidEmailAddress) {
      return
    }
    onEmailSubmit(rawEmailAddress)
  }

  const [isError, errorMessages] = useMemo(() => {
    if (rawEmailAddress === '') {
      return [false, null]
    }

    const messages = []
    if (invalidEmailAddress) {
      messages.push(STRING.account.addNewEmailAddressModal.emailAddressAddForm.invalidEmailAddressMessage)
    }

    if (isSameEmailAddress) {
      messages.push(STRING.account.addNewEmailAddressModal.emailAddressAddForm.sameEmailMessage)
    }

    if (isSendVerificationEmailFailed) {
      let errorMessage
      switch (errorCode) {
        case IDENTIFIER_ADD_ENDPOINT_ERROR_CODES.INVALID_IDENTIFIER_ERROR:
          errorMessage = STRING.account.addNewEmailAddressModal.errorMessages.invalidEmailIdentifierError
          break
        case IDENTIFIER_ADD_ENDPOINT_ERROR_CODES.IDENTIFIER_LIMIT_ERROR:
          errorMessage = STRING.account.addNewEmailAddressModal.errorMessages.identifierEmailLimitError
          break
        case IDENTIFIER_ADD_ENDPOINT_ERROR_CODES.IDENTIFIER_TAKEN_ERROR:
          errorMessage = STRING.account.addNewEmailAddressModal.errorMessages.identifierEmailTakenError
          break
        case IDENTIFIER_ADD_ENDPOINT_ERROR_CODES.COGNITO_POOL_ERROR:
          errorMessage = STRING.account.addNewEmailAddressModal.errorMessages.cognitoPoolError
          break
        default:
          errorMessage = STRING.account.addNewEmailAddressModal.errorMessages.defaultError
      }
      messages.push(errorMessage)
    }

    if (messages.length === 0) {
      return [false, null]
    }

    return [true, messages.map((message) => <div key={message}>{message}</div>)]
  }, [rawEmailAddress, invalidEmailAddress, isSameEmailAddress, isSendVerificationEmailFailed, errorCode])

  return (
    <>
      <ModalTitle>{STRING.account.addNewEmailAddressModal.emailAddressAddForm.addTitle}</ModalTitle>
      <ModalDescription>{STRING.account.addNewEmailAddressModal.emailAddressAddForm.addText}</ModalDescription>
      <Form onSubmit={handleSubmitClick}>
        <TextInput
          autoComplete="username"
          type="email"
          value={rawEmailAddress}
          placeholder={STRING.account.addNewEmailAddressModal.emailAddressAddForm.emailAddressPlaceholder}
          onChange={handleEmailChange}
          invalid={isError}
          errorMessage={errorMessages}
          disabled={isEmailSubmitting}
        />
        <Button
          onClick={handleSubmitClick}
          disabled={invalidEmailAddress || isEmailSubmitting || isSameEmailAddress || isSendVerificationEmailFailed}
          isLoading={isEmailSubmitting}
          type="submit"
        >
          {STRING.account.addNewEmailAddressModal.emailAddressAddForm.buttonText}
        </Button>
      </Form>
    </>
  )
}

EmailAddressAddForm.propTypes = {
  emailAddressData: PropTypes.arrayOf(
    PropTypes.shape({
      emailAddress: PropTypes.string.isRequired,
      isPrimary: PropTypes.bool.isRequired,
    }),
  ).isRequired,
  setPendingEmailAddress: PropTypes.func.isRequired,
  setScreenToEmailConfirmation: PropTypes.func.isRequired,
  autoSubmitEmailAddress: PropTypes.string,
}

export default EmailAddressAddForm
